// HeritageGallery — image carousel for the homepage "Our Heritage"
// section. Matches the thumbnail-strip pattern used by
// GastronomyGallery and PropertyCardGallery: one large main image
// that cross-fades between frames + a dark thumbnail strip below with
// a gold-outline active state. The BrandMonogramEdge anchors the
// visual identity to the corner of the main frame.
//
// Curated defaults are landscape-and-heritage photos across the group
// (Sahara dunes, Atlas peaks, kasbah walls, a bivouac at dusk, mint
// tea ritual, Berber weaving). All are CMS-swappable via the
// `data-cms-role="thumb.{idx}"` handles on both main and thumbnail so
// the two stay perfectly in sync (main image N reads the same slot as
// thumbnail N — the desync fix from Iteration 39 applied here from
// day one).

import React, { useState } from "react";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import OptimizedImage from "@/components/OptimizedImage";

const DEFAULT_HERITAGE_IMAGES = [
  {
    src: "https://images.pexels.com/photos/35771005/pexels-photo-35771005.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Sahara dunes at golden hour",
  },
  {
    src: "https://images.pexels.com/photos/13006180/pexels-photo-13006180.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Kasbah walls in the Atlas foothills",
  },
  {
    src: "https://images.pexels.com/photos/5490778/pexels-photo-5490778.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Moroccan mint tea ritual",
  },
  {
    src: "https://images.pexels.com/photos/1731660/pexels-photo-1731660.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Bivouac lanterns at dusk in Erg Chebbi",
  },
  {
    src: "https://images.pexels.com/photos/6272232/pexels-photo-6272232.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Berber craftsmanship — hand-woven textiles",
  },
  {
    src: "https://images.pexels.com/photos/12038028/pexels-photo-12038028.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "High Atlas peaks at sunrise",
  },
  {
    src: "https://images.pexels.com/photos/4275885/pexels-photo-4275885.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Marrakech medina archway with zellige",
  },
  {
    src: "https://images.pexels.com/photos/13820203/pexels-photo-13820203.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Traditional Moroccan zellige tilework",
  },
  {
    src: "https://images.pexels.com/photos/2949637/pexels-photo-2949637.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Camel caravan crossing the dunes at sunset",
  },
  {
    src: "https://images.pexels.com/photos/17849879/pexels-photo-17849879.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Ancient ksar architecture — Aït Ben Haddou",
  },
];

const HeritageGallery = ({
  initialSrc,
  testIdPrefix = "heritage-gallery",
  scope = "home.heritage.gallery",
}) => {
  const images = React.useMemo(() => {
    const pool = [];
    const seen = new Set();
    const push = (img) => {
      if (!img || !img.src || seen.has(img.src)) return;
      seen.add(img.src);
      pool.push(img);
    };
    if (initialSrc) push({ src: initialSrc, alt: "Grup Xaluca heritage" });
    DEFAULT_HERITAGE_IMAGES.forEach(push);
    return pool;
  }, [initialSrc]);

  const [activeIdx, setActiveIdx] = useState(0);
  const [brokenUrls, setBrokenUrls] = useState(() => new Set());

  const markBroken = (url) =>
    setBrokenUrls((cur) => {
      if (cur.has(url)) return cur;
      const next = new Set(cur);
      next.add(url);
      return next;
    });

  const visible = images.filter((img) => !brokenUrls.has(img.src));
  const safeIdx = Math.min(Math.max(activeIdx, 0), Math.max(visible.length - 1, 0));

  if (visible.length === 0) return null;

  return (
    <div
      className="block"
      data-testid={testIdPrefix}
      data-cms-scope={scope || undefined}
    >
      {/* Main image — cross-fades between frames. Height matches the
          original single image (h-[60vh]) so the section keeps the
          same vertical rhythm on desktop; slightly shorter on mobile
          to keep the two-column stack from feeling too tall. */}
      <div
        className="relative block overflow-hidden h-[52vh] md:h-[60vh]"
        data-testid={`${testIdPrefix}-main`}
      >
        {visible.map((img, idx) => (
          <OptimizedImage
            key={img.src}
            src={img.src}
            alt={img.alt}
            priority={idx === 0}
            sizes="(min-width: 768px) 50vw, 100vw"
            onError={() => markBroken(img.src)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out ${
              idx === safeIdx ? "opacity-100" : "opacity-0"
            }`}
            objectFit="cover"
            data-cms-role={`heritage.${idx}`}
          />
        ))}
        <BrandMonogramEdge
          size="lg"
          opacity={0.5}
          testIdPrefix={`${testIdPrefix}-brand`}
        />
      </div>

      {/* Thumbnail strip — same dark band + gold-outline active state
          as the gastronomy and hotel-card galleries, so the CMS-driven
          image UX reads uniformly across the homepage. */}
      {visible.length > 1 ? (
        <div
          className="bg-[#0F0B09] px-3 py-3 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid={`${testIdPrefix}-thumbs`}
        >
          <div className="flex items-center gap-2 min-w-min">
            {visible.map((img, idx) => {
              const active = idx === safeIdx;
              return (
                <button
                  type="button"
                  key={`heritage-thumb-${img.src}`}
                  onClick={() => setActiveIdx(idx)}
                  className={`relative shrink-0 w-16 sm:w-20 aspect-[4/3] overflow-hidden transition-all duration-300 ${
                    active
                      ? "outline outline-2 outline-offset-2 outline-[#D4A373] opacity-100"
                      : "opacity-70 hover:opacity-100"
                  }`}
                  aria-label={`Show heritage image ${idx + 1}`}
                  aria-current={active}
                  data-testid={`${testIdPrefix}-thumb-${idx}`}
                >
                  <OptimizedImage
                    src={img.src}
                    alt=""
                    widths={[80, 160, 240]}
                    sizes="80px"
                    onError={() => markBroken(img.src)}
                    className="absolute inset-0 w-full h-full object-cover"
                    aria-hidden="true"
                    objectFit="cover"
                    data-cms-role={`heritage.${idx}`}
                  />
                  {!active ? (
                    <span
                      className="absolute inset-0 bg-[#1A1513]/30 pointer-events-none"
                      aria-hidden="true"
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default HeritageGallery;
